import { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Library,
  Layers,
  Users,
  Zap,
  ChevronDown,
  ChevronUp,
  Activity,
  Sparkles,
  Search,
  CheckCircle2,
  XCircle,
} from 'lucide-react'
import { PageHeader, fadeUp, staggerContainer, staggerItem } from '@personic/shared'
import { buildBreadcrumbs } from '@/config/routes'
import { usePersonaEngineArchetypes, usePersonaEngineHealth } from '@/hooks/usePersonaEngine'
import type { PersonaEngineArchetype, ArchetypeCategory } from '@personic/shared/types/persona-engine'
import { ENGINE_SHORT_LABELS } from '@personic/shared/types/persona-engine'

// =============================================================================
// CONSTANTS
// =============================================================================

type TabFilter = 'all' | 'companion' | 'assistant' | 'engage_stock'

const TABS: Array<{ key: TabFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'companion', label: 'Identity Companions' },
  { key: 'assistant', label: 'Identity Assistants' },
  { key: 'engage_stock', label: 'Engage Stock' },
]

const CATEGORY_STYLES: Record<ArchetypeCategory, { bg: string; text: string; border: string }> = {
  companion: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  assistant: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  engage_stock: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
}

const PRODUCT_CONTEXT_STYLES: Record<string, { bg: string; text: string }> = {
  identity: { bg: 'bg-teal-50', text: 'text-teal-700' },
  b2b: { bg: 'bg-copper-50', text: 'text-copper-700' },
}

const DIMENSION_COLORS: string[] = [
  'bg-copper-500',
  'bg-blue-500',
  'bg-purple-500',
  'bg-teal-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-indigo-500',
  'bg-emerald-500',
  'bg-orange-500',
]

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function ArchetypeLibrary() {
  const breadcrumbs = buildBreadcrumbs('/admin/persona-engine/archetypes')
  const [activeTab, setActiveTab] = useState<TabFilter>('all')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const { data: archetypes, isLoading, error } = usePersonaEngineArchetypes()
  const { data: health } = usePersonaEngineHealth()

  // Derived stats
  const stats = useMemo(() => {
    if (!archetypes) return { total: 0, active: 0, identity: 0, engage: 0 }
    return {
      total: archetypes.length,
      active: archetypes.filter((a) => a.is_active).length,
      identity: archetypes.filter((a) => a.product_context === 'identity').length,
      engage: archetypes.filter((a) => a.product_context === 'b2b').length,
    }
  }, [archetypes])

  // Filter by tab + search
  const filtered = useMemo(() => {
    if (!archetypes) return []
    let result = archetypes

    if (activeTab !== 'all') {
      result = result.filter((a) => a.category === activeTab)
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      result = result.filter(
        (a) =>
          a.display_name.toLowerCase().includes(q) ||
          a.description.toLowerCase().includes(q) ||
          a.archetype_id.toLowerCase().includes(q)
      )
    }

    return result
  }, [archetypes, activeTab, searchQuery])

  const toggleExpand = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id))
  }

  return (
    <div>
      <PageHeader
        title="Archetype Library"
        description="Browse and inspect the v3.0 archetype catalog powering the Persona Engine."
        breadcrumbs={breadcrumbs}
      />

      {/* Summary stat cards */}
      <motion.div
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6"
      >
        <StatCard
          label="Total Archetypes"
          value={isLoading ? '--' : String(stats.total)}
          icon={Library}
          color="copper"
        />
        <StatCard
          label="Active"
          value={isLoading ? '--' : String(stats.active)}
          icon={Activity}
          color="emerald"
        />
        <StatCard
          label="Identity"
          value={isLoading ? '--' : String(stats.identity)}
          icon={Users}
          color="teal"
        />
        <StatCard
          label="Engage"
          value={isLoading ? '--' : String(stats.engage)}
          icon={Zap}
          color="amber"
        />
      </motion.div>

      {/* Health banner */}
      {health && (
        <motion.div variants={fadeUp} initial="hidden" animate="visible" className="mb-6">
          <div className="rounded-xl border border-warm-200 bg-white px-5 py-3 flex items-center gap-3 text-xs text-warm-500">
            <span
              className={`inline-block w-2 h-2 rounded-full ${
                health.status === 'healthy' ? 'bg-emerald-400' : 'bg-amber-400'
              }`}
            />
            <span>
              Persona Engine {health.version} &middot; {health.engines_available.length} engines &middot;{' '}
              {health.archetype_count} archetypes registered
            </span>
          </div>
        </motion.div>
      )}

      {/* Search bar */}
      <motion.div variants={fadeUp} initial="hidden" animate="visible" className="mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search archetypes..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-warm-200 bg-white text-sm text-midnight-800 placeholder-warm-400 focus:outline-none focus:border-copper-300 focus:ring-1 focus:ring-copper-200 transition-colors"
          />
        </div>
      </motion.div>

      {/* Tab filter */}
      <motion.div variants={fadeUp} initial="hidden" animate="visible" className="mb-6">
        <div className="flex gap-1 bg-warm-100 rounded-xl p-1 w-fit">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                activeTab === tab.key
                  ? 'bg-white text-midnight-800 shadow-sm'
                  : 'text-warm-500 hover:text-midnight-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Loading skeletons */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 9 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      )}

      {/* Error state */}
      {error && (
        <motion.div variants={fadeUp} initial="hidden" animate="visible">
          <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
            <p className="text-sm text-red-600">Failed to load archetypes. Check Persona Engine health status.</p>
          </div>
        </motion.div>
      )}

      {/* Empty state */}
      {!isLoading && !error && filtered.length === 0 && (
        <motion.div variants={fadeUp} initial="hidden" animate="visible">
          <div className="rounded-xl border border-warm-200 bg-white p-12 text-center">
            <Layers className="w-10 h-10 text-warm-300 mx-auto mb-3" />
            <p className="text-sm text-warm-500">
              {searchQuery ? `No archetypes match "${searchQuery}"` : 'No archetypes in this category.'}
            </p>
          </div>
        </motion.div>
      )}

      {/* Archetype card grid */}
      {!isLoading && !error && filtered.length > 0 && (
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
        >
          {filtered.map((archetype) => (
            <ArchetypeCard
              key={archetype.archetype_id}
              archetype={archetype}
              isExpanded={expandedId === archetype.archetype_id}
              onToggle={() => toggleExpand(archetype.archetype_id)}
            />
          ))}
        </motion.div>
      )}

      {/* Count footer */}
      {!isLoading && filtered.length > 0 && (
        <motion.div variants={fadeUp} initial="hidden" animate="visible" className="mt-6 text-center">
          <p className="text-xs text-warm-400">
            Showing {filtered.length} of {archetypes?.length ?? 0} archetypes
            {searchQuery && ` matching "${searchQuery}"`}
          </p>
        </motion.div>
      )}
    </div>
  )
}

// =============================================================================
// STAT CARD
// =============================================================================

function StatCard({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string
  value: string
  icon: React.ElementType
  color: 'copper' | 'emerald' | 'teal' | 'amber'
}) {
  const colorMap = {
    copper: { bg: 'bg-copper-50', icon: 'text-copper-500' },
    emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-500' },
    teal: { bg: 'bg-teal-50', icon: 'text-teal-500' },
    amber: { bg: 'bg-amber-50', icon: 'text-amber-500' },
  }

  const c = colorMap[color]

  return (
    <motion.div
      variants={staggerItem}
      className="rounded-xl border border-warm-200 bg-white p-5"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-9 h-9 rounded-lg ${c.bg} flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${c.icon}`} />
        </div>
      </div>
      <p className="text-2xl font-heading font-bold text-midnight-900">{value}</p>
      <p className="text-xs text-warm-500 mt-0.5">{label}</p>
    </motion.div>
  )
}

// =============================================================================
// ARCHETYPE CARD
// =============================================================================

function ArchetypeCard({
  archetype,
  isExpanded,
  onToggle,
}: {
  archetype: PersonaEngineArchetype
  isExpanded: boolean
  onToggle: () => void
}) {
  const catStyle = CATEGORY_STYLES[archetype.category] ?? CATEGORY_STYLES.companion
  const ctxStyle = PRODUCT_CONTEXT_STYLES[archetype.product_context] ?? {
    bg: 'bg-warm-50',
    text: 'text-warm-600',
  }

  const truncatedDesc =
    archetype.description.length > 120
      ? archetype.description.slice(0, 120) + '...'
      : archetype.description

  return (
    <motion.div
      variants={staggerItem}
      layout
      className="rounded-xl border border-warm-200 bg-white hover:border-copper-300 hover:shadow-sm transition-all"
    >
      <button
        onClick={onToggle}
        className="w-full text-left p-5 focus:outline-none focus:ring-2 focus:ring-copper-200 rounded-xl"
      >
        {/* Header row */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-semibold text-midnight-800 text-sm leading-tight">
              {archetype.display_name}
            </h3>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {archetype.is_active ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            ) : (
              <XCircle className="w-4 h-4 text-warm-300" />
            )}
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 text-warm-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-warm-400" />
            )}
          </div>
        </div>

        {/* Badges */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${catStyle.bg} ${catStyle.text} border ${catStyle.border}`}
          >
            {formatCategory(archetype.category)}
          </span>
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${ctxStyle.bg} ${ctxStyle.text}`}
          >
            {archetype.product_context}
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-warm-50 text-warm-600">
            <Sparkles className="w-3 h-3" />
            {ENGINE_SHORT_LABELS[archetype.default_engine] ?? archetype.default_engine}
          </span>
        </div>

        {/* Description */}
        <p className="text-xs text-warm-500 leading-relaxed">{truncatedDesc}</p>
      </button>

      {/* Expanded detail */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 border-t border-warm-100 pt-4 space-y-4">
              {/* Persona dimensions bar chart */}
              <DimensionChart dimensions={archetype.base_persona_object} />

              {/* Authored narrative */}
              {archetype.authored_narrative && (
                <div>
                  <p className="text-[10px] font-medium text-warm-400 uppercase tracking-wider mb-1">
                    Authored Narrative
                  </p>
                  <p className="text-xs text-warm-600 leading-relaxed bg-warm-50 rounded-lg p-3">
                    {archetype.authored_narrative.length > 300
                      ? archetype.authored_narrative.slice(0, 300) + '...'
                      : archetype.authored_narrative}
                  </p>
                </div>
              )}

              {/* Platform variants */}
              {archetype.platform_variants &&
                Object.keys(archetype.platform_variants).length > 0 && (
                  <div>
                    <p className="text-[10px] font-medium text-warm-400 uppercase tracking-wider mb-1.5">
                      Platform Variants
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.keys(archetype.platform_variants).map((key) => (
                        <span
                          key={key}
                          className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-copper-50 text-copper-600 border border-copper-200"
                        >
                          {key}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

              {/* Metadata row */}
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-warm-400 pt-1 border-t border-warm-100">
                <span>
                  ID: <span className="text-warm-500 font-mono">{archetype.archetype_id}</span>
                </span>
                <span>
                  Version: <span className="text-warm-500">{archetype.version}</span>
                </span>
                <span>
                  Threshold:{' '}
                  <span className="text-warm-500">{archetype.min_confidence_threshold}</span>
                </span>
                {archetype.warmth_floor_override !== null && (
                  <span>
                    Warmth floor:{' '}
                    <span className="text-warm-500">{archetype.warmth_floor_override}</span>
                  </span>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

// =============================================================================
// DIMENSION CHART
// =============================================================================

function DimensionChart({ dimensions }: { dimensions: Record<string, number> }) {
  const entries = Object.entries(dimensions)
  if (entries.length === 0) {
    return (
      <p className="text-[10px] text-warm-400 italic">No base persona dimensions defined.</p>
    )
  }

  return (
    <div>
      <p className="text-[10px] font-medium text-warm-400 uppercase tracking-wider mb-2">
        Base Persona ({entries.length} dimensions)
      </p>
      <div className="space-y-1.5">
        {entries.map(([dim, value], idx) => {
          const pct = Math.round(Math.min(Math.max(value, 0), 1) * 100)
          const colorClass = DIMENSION_COLORS[idx % DIMENSION_COLORS.length]

          return (
            <div key={dim} className="flex items-center gap-2">
              <span className="text-[10px] text-warm-500 w-24 truncate flex-shrink-0" title={dim}>
                {formatDimensionLabel(dim)}
              </span>
              <div className="flex-1 h-2 bg-warm-100 rounded-full overflow-hidden">
                <span
                  className={`block h-full rounded-full ${colorClass}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="text-[10px] text-warm-400 w-8 text-right font-mono">
                {value.toFixed(2)}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// =============================================================================
// SKELETON CARD
// =============================================================================

function SkeletonCard() {
  return (
    <div className="rounded-xl border border-warm-200 bg-white p-5 animate-pulse">
      <div className="flex items-start justify-between mb-3">
        <div className="h-4 bg-warm-100 rounded w-2/3" />
        <div className="h-4 w-4 bg-warm-100 rounded" />
      </div>
      <div className="flex gap-1.5 mb-3">
        <div className="h-5 bg-warm-100 rounded w-16" />
        <div className="h-5 bg-warm-100 rounded w-12" />
        <div className="h-5 bg-warm-100 rounded w-10" />
      </div>
      <div className="space-y-1.5">
        <div className="h-3 bg-warm-100 rounded w-full" />
        <div className="h-3 bg-warm-100 rounded w-4/5" />
      </div>
    </div>
  )
}

// =============================================================================
// HELPERS
// =============================================================================

function formatCategory(category: ArchetypeCategory): string {
  switch (category) {
    case 'companion':
      return 'Companion'
    case 'assistant':
      return 'Assistant'
    case 'engage_stock':
      return 'Engage Stock'
    default:
      return category
  }
}

function formatDimensionLabel(dim: string): string {
  return dim
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}
